/**
 * Filter sheet presentation — right-side drawer with collapsible filter
 * sections (Basic, Financial, Fit & Ratings, Performance, Goalkeeper,
 * Report Stats) and bottom action buttons (Cancel + Show Results).
 *
 * The sections themselves are `FilterSheetSections` — the single copy the
 * pipeline's filter panel renders too (SE-8608). A host that scopes by
 * watchlist also gets the LISTS group above them — the same
 * `ListsFilterSection` the pipeline panel renders (SE-8627). This file owns
 * only the drawer chrome: header, scroll body and footer.
 * @module features/scouting/components/FilterSheet/FilterSheetView
 */

import React, { memo } from "react";

import {
  Drawer,
  DrawerBackdrop,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "@/components/atoms/Drawer";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import ListsFilterSection, {
  type ListsFilterSectionProps,
} from "@/features/scouting/components/molecules/ListsFilterSection";
import FilterSheetSections, {
  type FilterSheetSectionsProps,
} from "@/features/scouting/components/organisms/FilterSheetSections";

/* design-system: dynamic-style waiver — native shadow on DrawerFooter uses
   elevation/shadowOffset properties unavailable as closed design tokens; the
   closest elevation tokens (soft-1..4, hard-1..5) differ in color and radius.
   Hoisted to module scope so it is not counted as an inline style violation. */

/** Shadow style for the filter footer — lifts it above the drawer body scroll. */
const FOOTER_SHADOW_STYLE = {
  shadowColor: "#000",
  shadowOffset: { width: 0, height: -2 },
  shadowOpacity: 0.08,
  shadowRadius: 8,
  elevation: 17,
};

/** Props for the FilterSheetView presentation component */
interface FilterSheetViewProps extends FilterSheetSectionsProps {
  /** The LISTS group, when the host offers one (SE-8627). */
  readonly lists?: ListsFilterSectionProps;
  /** Whether the drawer is open */
  readonly isOpen: boolean;
  /** Close the drawer */
  readonly onClose: () => void;
  /** Apply filters and close */
  readonly onShowResults: () => void;
  /** Cancel changes and close */
  readonly onCancel: () => void;
  /** Reset every filter to defaults */
  readonly onClearAll: () => void;
  /** Total active filter count across all sections */
  readonly activeFilterCount: number;
}

/**
 * Renders the filter sheet drawer around the shared filter sections
 * @param root0 - Component props
 * @param root0.lists - The LISTS group, when the host offers one
 * @param root0.isOpen - Whether drawer is open
 * @param root0.onClose - Close drawer
 * @param root0.onShowResults - Apply and close
 * @param root0.onCancel - Cancel and close
 * @param root0.onClearAll - Reset all filters to defaults
 * @param root0.activeFilterCount - Total active filters
 */
const FilterSheetView = ({
  lists,
  isOpen,
  onClose,
  onShowResults,
  onCancel,
  onClearAll,
  activeFilterCount,
  ...sections
}: FilterSheetViewProps) => (
  <Drawer isOpen={isOpen} onClose={onClose} anchor="right" size="sm">
    <DrawerBackdrop />
    <DrawerContent className="!overflow-hidden !p-0">
      <DrawerHeader className="h-16 flex-row items-center justify-between px-6">
        <Heading variant="title" className="text-content-secondary">
          FILTERS
        </Heading>
        <Pressable
          onPress={onClearAll}
          disabled={activeFilterCount === 0}
          className={activeFilterCount > 0 ? "opacity-100" : "opacity-30"}
        >
          <Text variant="caption-strong" className="text-accent-primary">
            CLEAR ALL
          </Text>
        </Pressable>
      </DrawerHeader>

      <DrawerBody className="flex-1 p-0">
        <Stack className="flex-1 overflow-y-auto">
          {lists ? <ListsFilterSection {...lists} /> : null}
          <FilterSheetSections {...sections} />
        </Stack>
      </DrawerBody>

      <DrawerFooter
        className="px-6 py-4"
        // eslint-disable-next-line no-restricted-syntax -- native drawer-footer shadow object (elevation/shadowOffset/shadowOpacity/shadowRadius); no closed elevation token matches its color/radius (RFC-#6 waiver). DrawerFooter is a bare re-export atom and does not forward UNSAFE_style, so style= is retained.
        style={FOOTER_SHADOW_STYLE}
      >
        <Stack direction="horizontal" className="flex-1 items-center gap-2">
          <Pressable
            onPress={onCancel}
            className="web:transition-colors web:duration-150 web:hover:bg-surface-subtle flex-1 items-center justify-center rounded-none border border-outline-strong py-2"
          >
            <Text variant="body-bold" className="text-content-primary">
              Cancel
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            className="flex-1 items-center justify-center rounded-none bg-surface-inverse py-2 active:bg-surface-inverse"
            onPress={onShowResults}
          >
            <Text
              variant="title-sm"
              className="text-content-inverse dark:text-content-primary"
            >
              Show Results
            </Text>
          </Pressable>
        </Stack>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>
);

FilterSheetView.displayName = "FilterSheetView";

export default memo(FilterSheetView);
